import {
    Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, IconButton, InputLabel,
    LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { TEXTO_COLORIDO_ESCURO, coresDoTema } from "assets/coresTema";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useVisualizadorArquivos } from "context/VisualizadorArquivosContext";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import { useCallback, useEffect, useMemo, useState } from "react";
import { get, getFile, post } from "utils/WebService";
import {
    FAROIS, FILTROS_SITUACAO, GRUPOS, SITUACOES, contarGrupos, correspondeBusca, dataCurta, dataHora, dataIso,
    formatarDuracao, formatarTamanho, grupoDoFarol, ordenarRobos, tempoRelativo,
} from "./monitorRobosUtils";

const ATUALIZACAO_OPTIONS = [
    { id: 0, value: 0, label: "Desativada" },
    { id: 30, value: 30, label: "30 s" },
    { id: 60, value: 60, label: "1 min" },
    { id: 300, value: 300, label: "5 min" },
];
const GRUPO_TODOS = { id: "", value: "", label: "Todos" };
const GRUPO_OPTIONS = [GRUPO_TODOS, ...Object.entries(GRUPOS).map(([value, g]) => ({ id: value, value, label: g.rotulo }))];
const rotulo = { px: 1, pb: 0.5, pt: 0.2 };
const celula = { verticalAlign: "top", pt: 1.5 };
const POR_PAGINA = 20;

/** Cor de um tom do tema (success, error...) no modo claro e no escuro; sem tom = cinza do texto fraco. */
function useCorDoTom() {
    const theme = useTheme();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);
    return useCallback((tom) => {
        if (!tom) return cores.textoFraco;
        return darkMode ? TEXTO_COLORIDO_ESCURO[tom] || cores.texto : theme.palette[tom]?.main || cores.texto;
    }, [darkMode, theme, cores]);
}

/** Selo de situação: ícone colorido + rótulo (a cor nunca vai sozinha). */
function Selo({ icone, tom, rotulo: texto, girar }) {
    const corDoTom = useCorDoTom();
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const cor = corDoTom(tom);
    return (
        <MDBox component="span" display="inline-flex" alignItems="center" gap={0.5}
            sx={{ px: 1, py: 0.25, borderRadius: "12px", border: `1px solid ${cor}`, whiteSpace: "nowrap" }}>
            <Icon sx={{
                color: cor, fontSize: "1rem !important",
                ...(girar ? { animation: "girarMonitor 1.6s linear infinite", "@keyframes girarMonitor": { to: { transform: "rotate(-360deg)" } } } : {}),
            }}>{icone}</Icon>
            <MDTypography component="span" variant="caption" fontWeight="bold" sx={{ color: cores.texto }}>{texto}</MDTypography>
        </MDBox>
    );
}

function Indicador({ grupo, valor, selecionado, onClick }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const corDoTom = useCorDoTom();
    const g = GRUPOS[grupo];
    const cor = corDoTom(g.tom);
    return (
        <Card onClick={onClick} role="button" tabIndex={0} aria-pressed={selecionado}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
            sx={{
                p: 2, height: "100%", cursor: "pointer", background: cores.cartao,
                boxShadow: selecionado ? `inset 0 0 0 2px ${cor}` : undefined,
                "&:hover": selecionado ? undefined : { boxShadow: `inset 0 0 0 1px ${cor}` },
            }}>
            <MDBox display="flex" alignItems="center" gap={2}>
                <Icon sx={{ color: cor, fontSize: "2rem !important" }}>{g.icone}</Icon>
                <MDBox minWidth={0}>
                    <MDTypography variant="button" color="text" sx={{ display: "block", fontWeight: 500 }}>{g.rotulo}</MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="text" sx={{ lineHeight: 1.2 }}>{valor}</MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>{g.detalhe}</MDTypography>
                </MDBox>
            </MDBox>
        </Card>
    );
}

function Instancias({ instancias, agora }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    if (!instancias.length) {
        return (
            <Card sx={{ p: 2, background: cores.cartao }}>
                <MDBox display="flex" alignItems="center" gap={1.5}>
                    <Icon sx={{ color: cores.textoFraco }}>dns</Icon>
                    <MDTypography variant="button" color="text">
                        Nenhum serviço de robôs registrado ainda. O serviço se registra ao subir (a partir da versão com o monitor)
                        e manda sinal de vida a cada minuto.
                    </MDTypography>
                </MDBox>
            </Card>
        );
    }
    return (
        <Card sx={{ px: 2, py: 1.5, background: cores.cartao }}>
            <Grid container spacing={2}>
                {instancias.map((i) => (
                    <Grid item xs={12} md={6} lg={4} key={i.maquina}>
                        <MDBox display="flex" alignItems="flex-start" gap={1.5}>
                            <Icon sx={{ mt: 0.25, color: cores.textoSecundario }}>dns</Icon>
                            <MDBox minWidth={0}>
                                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <MDTypography variant="button" fontWeight="bold" color="text">{i.maquina}</MDTypography>
                                    {i.ativa
                                        ? <Selo icone="favorite" tom="success" rotulo="No ar" />
                                        : <Selo icone="power_off" tom="error" rotulo="Sem sinal de vida" />}
                                </MDBox>
                                <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                                    Perfil {i.perfil || "-"} · {i.qtdRobos} robô{i.qtdRobos === 1 ? "" : "s"} · último sinal {tempoRelativo(i.ultimoSinal, agora)}
                                    {i.inicio ? ` · no ar desde ${dataCurta(i.inicio, agora)}` : ""}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    </Grid>
                ))}
            </Grid>
        </Card>
    );
}

function CartaoRobo({ robo, agora, onAbrir }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const corDoTom = useCorDoTom();
    const f = FAROIS[robo.farol] || FAROIS.AGUARDANDO;
    const cor = corDoTom(f.tom);
    const comErro = robo.farol === "ERRO" && robo.ultimoErroTexto;
    return (
        <Card onClick={onAbrir} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter") onAbrir(); }}
            sx={{
                p: 2, height: "100%", cursor: "pointer", background: cores.cartao, borderLeft: `4px solid ${cor}`,
                "&:hover": { background: cores.hover },
            }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
                <MDBox minWidth={0}>
                    <MDTypography variant="button" fontWeight="bold" color="text" sx={{ display: "block", lineHeight: 1.3 }}>{robo.nome}</MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ display: "block", color: cores.textoFraco }}>{robo.agendaDescricao}</MDTypography>
                </MDBox>
                <Tooltip title={robo.farolDescricao || ""} placement="top">
                    <span><Selo icone={f.icone} tom={f.tom} rotulo={robo.farolLabel} girar={robo.farol === "EM_EXECUCAO"} /></span>
                </Tooltip>
            </MDBox>
            <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                {robo.ultimoInicio
                    ? <>Última: <b>{tempoRelativo(robo.ultimoInicio, agora)}</b>
                        {robo.farol !== "EM_EXECUCAO" && robo.ultimaDuracao !== null && robo.ultimaDuracao !== undefined ? ` · durou ${formatarDuracao(robo.ultimaDuracao)}` : ""}
                        {robo.ultimaSituacaoLabel ? ` · ${robo.ultimaSituacaoLabel}` : ""}</>
                    : "Ainda não rodou"}
            </MDTypography>
            {robo.ultimoResumo && (
                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>{robo.ultimoResumo}</MDTypography>
            )}
            {robo.aviso && (
                <MDTypography variant="caption" sx={{ display: "block", mt: 0.25, color: corDoTom("info"), fontWeight: "bold" }}>{robo.aviso}</MDTypography>
            )}
            {comErro && (
                <MDTypography variant="caption" sx={{ display: "block", mt: 0.25, color: corDoTom("error"), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {robo.ultimoErroTexto.split("\n")[0]}
                </MDTypography>
            )}
            <MDBox display="flex" justifyContent="space-between" flexWrap="wrap" gap={1} mt={1}>
                <MDTypography variant="caption" color="text" sx={{ color: cores.textoFraco }}>
                    {/* Serviço parado: a próxima prevista já passou, só roda quando o serviço voltar */}
                    {robo.farol === "PARADO" ? "Próxima: quando o serviço voltar"
                        : robo.proximaExecucao ? `Próxima: ${dataCurta(robo.proximaExecucao, agora)}` : ""}
                </MDTypography>
                <MDTypography variant="caption" color="text" sx={{ color: cores.textoFraco }}>
                    24h: {robo.erros24h > 0 ? <b style={{ color: corDoTom("error") }}>{robo.erros24h} com erro</b> : "sem erro"}
                    {robo.processados24h > 0 ? ` · ${robo.processados24h} processado${robo.processados24h === 1 ? "" : "s"}` : ""}
                </MDTypography>
            </MDBox>
        </Card>
    );
}

function Campo({ titulo, children }) {
    return (
        <MDBox>
            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block" }}>{titulo}</MDTypography>
            <MDTypography variant="caption" color="text" component="div">{children}</MDTypography>
        </MDBox>
    );
}

function TextoErro({ texto }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    return (
        <MDBox component="pre" sx={{
            m: 0, p: 1.5, maxHeight: 320, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word",
            fontSize: "0.72rem", lineHeight: 1.45, background: cores.painelEscuro, color: cores.texto,
            border: `1px solid ${cores.borda}`, borderRadius: "6px",
        }}>{texto}</MDBox>
    );
}

/** Uma execução: erro completo e arquivos (o CSV enviado e o retorno do WIBS abrem no visualizador). */
function DetalheExecucao({ id, onFechar, onErro }) {
    const { abrirArquivos } = useVisualizadorArquivos();
    const [execucao, setExecucao] = useState(null);
    useEffect(() => {
        if (!id) return;
        setExecucao(null);
        get(`/robo-monitor/execucoes/${encodeURIComponent(id)}`)
            .then(setExecucao)
            .catch((e) => { onErro(e?.message || "Não foi possível abrir a execução."); onFechar(); });
    }, [id, onErro, onFechar]);
    const s = execucao ? SITUACOES[execucao.situacao] || {} : {};
    const arquivos = (execucao?.arquivos || []).map((a) => ({
        ...a,
        guardado: a.tamanho !== null && a.tamanho !== undefined,
        url: `/robo-monitor/arquivos/${encodeURIComponent(a.id)}`,
        rotulo: a.tipo === "ENVIADO" ? "Enviado" : a.tipo === "RETORNO" ? "Retorno" : a.tipo === "TELA" ? "Tela no erro" : a.tipo,
    }));
    // O visualizador navega entre os arquivos da execução (ex.: do CSV enviado para o retorno do WIBS)
    const visualizaveis = arquivos.filter((a) => a.guardado).map((a) => ({ url: a.url, nome: a.nome, tipo: a.rotulo }));
    return (
        <Dialog open={!!id} onClose={onFechar} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                Execução {execucao ? `de ${dataHora(execucao.inicio)}` : ""}
                {execucao && <Selo icone={s.icone || "help"} tom={s.tom} rotulo={execucao.situacaoLabel || "-"} />}
            </DialogTitle>
            <DialogContent dividers>
                {!execucao ? (
                    <MDBox display="flex" justifyContent="center" p={4}><CircularProgress color="info" /></MDBox>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}><Campo titulo="Robô">{execucao.nome}</Campo></Grid>
                        <Grid item xs={6} sm={3}><Campo titulo="Máquina">{execucao.maquina || "-"}</Campo></Grid>
                        <Grid item xs={6} sm={3}><Campo titulo="Fim">{execucao.fim ? dataHora(execucao.fim) : "-"}</Campo></Grid>
                        <Grid item xs={6} sm={3}><Campo titulo="Duração">{formatarDuracao(execucao.duracao)}</Campo></Grid>
                        <Grid item xs={6} sm={3}><Campo titulo="Processados">{execucao.processados ?? "-"}</Campo></Grid>
                        <Grid item xs={6} sm={3}><Campo titulo="Erros no log">{execucao.erros ?? "-"}</Campo></Grid>
                        <Grid item xs={12} sm={6}><Campo titulo="Resumo">{execucao.resumo || "-"}</Campo></Grid>
                        <Grid item xs={12}>
                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", mb: 0.5 }}>Arquivos</MDTypography>
                            {arquivos.length === 0 ? (
                                <MDTypography variant="caption" color="text">Nenhum arquivo guardado nesta execução.</MDTypography>
                            ) : arquivos.map((a) => (
                                <MDBox key={a.id || a.nome} display="flex" alignItems="center" gap={1} flexWrap="wrap" py={0.5}>
                                    <Icon fontSize="small">{a.tipo === "RETORNO" ? "move_to_inbox" : a.tipo === "TELA" ? "photo_camera" : "outbox"}</Icon>
                                    <MDTypography variant="caption" color="text" sx={{ minWidth: 0, wordBreak: "break-all" }}>
                                        <b>{a.rotulo}</b>: {a.nome} ({formatarTamanho(a.tamanho)})
                                    </MDTypography>
                                    {a.guardado ? (
                                        <>
                                            <MDButton variant="text" color="info" size="small"
                                                onClick={() => abrirArquivos(visualizaveis, visualizaveis.findIndex((v) => v.url === a.url))}>
                                                <Icon sx={{ mr: 0.5 }}>visibility</Icon>Ver
                                            </MDButton>
                                            <MDButton variant="text" color="info" size="small" onClick={() => getFile(a.url, a.nome).catch((e) => onErro(e?.message))}>
                                                <Icon sx={{ mr: 0.5 }}>download</Icon>Baixar
                                            </MDButton>
                                        </>
                                    ) : (
                                        <MDTypography variant="caption" color="text">(maior que 10 MB: só o nome foi registrado)</MDTypography>
                                    )}
                                </MDBox>
                            ))}
                        </Grid>
                        {execucao.erro && (
                            <Grid item xs={12}>
                                <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", mb: 0.5 }}>Erros</MDTypography>
                                <TextoErro texto={execucao.erro} />
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <MDButton variant="text" color="info" onClick={onFechar}>Fechar</MDButton>
            </DialogActions>
        </Dialog>
    );
}

const INTERVALO_TELA_MS = 4000;
// Foto mais velha que isso não é "ao vivo": o robô terminou (o painel ainda não atualizou) ou parou de fotografar
const FOTO_AO_VIVO_MS = 15000;

const horaComSegundos = (data) => new Date(data).toLocaleTimeString("pt-BR");

/**
 * Tela da máquina do robô: enquanto ele trabalha, a última foto da tela, atualizada a cada 4 s (o ral-robot fotografa
 * a tela do Windows, TelaDaMaquina). Fora da execução, só a referência da última foto, que abre ao clicar.
 */
function TelaAgora({ robo }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const [tela, setTela] = useState(null);
    const [carregou, setCarregou] = useState(false);
    const [verUltima, setVerUltima] = useState(false);
    const [ampliada, setAmpliada] = useState(false);
    const trabalhando = robo?.farol === "EM_EXECUCAO";
    const maquina = robo?.maquina;

    useEffect(() => {
        if (!maquina) return undefined;
        let ativo = true;
        const carregar = () => get(`/robo-monitor/tela/${encodeURIComponent(maquina)}`)
            .then((t) => { if (ativo) { setTela(t || null); setCarregou(true); } })
            .catch(() => { if (ativo) setCarregou(true); });
        carregar();
        const timer = trabalhando ? setInterval(carregar, INTERVALO_TELA_MS) : null;
        return () => { ativo = false; if (timer) clearInterval(timer); };
    }, [maquina, trabalhando]);

    // Clicar alterna entre caber na janela e o tamanho real (com dois monitores, a foto fica larga e pequena)
    const imagem = tela?.imagem ? (
        <MDBox sx={{ mt: 1, overflow: "auto", maxHeight: ampliada ? 600 : "none", border: `1px solid ${cores.borda}`, borderRadius: "6px" }}>
            <MDBox component="img" src={`data:image/jpeg;base64,${tela.imagem}`} alt={`Tela da máquina ${maquina}`}
                title={ampliada ? "Clique para caber na janela" : "Clique para ver no tamanho real"}
                onClick={() => setAmpliada((a) => !a)}
                sx={{ display: "block", width: ampliada ? "auto" : "100%", maxWidth: ampliada ? "none" : "100%",
                    cursor: ampliada ? "zoom-out" : "zoom-in" }} />
        </MDBox>
    ) : null;
    const aoVivo = trabalhando && tela?.data && Date.now() - tela.data < FOTO_AO_VIVO_MS;

    return (
        <MDBox>
            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", mb: 0.5 }}>
                Tela da máquina {maquina || ""}
            </MDTypography>
            {!carregou ? (
                <CircularProgress color="info" size={18} />
            ) : trabalhando ? (
                tela?.aviso ? (
                    <MDTypography variant="caption" color="warning" sx={{ display: "block" }}>{tela.aviso}</MDTypography>
                ) : tela?.imagem ? (
                    <>
                        <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                            {aoVivo ? "Ao vivo, atualiza a cada 4 s · foto" : "Última foto (o robô terminou ou parou de enviar fotos)"} das {horaComSegundos(tela.data)}
                            {tela.robos ? ` · trabalhando: ${tela.robos}` : ""}
                        </MDTypography>
                        {imagem}
                    </>
                ) : (
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                        Aguardando a primeira foto (o robô fotografa a tela a cada 4 s enquanto trabalha).
                    </MDTypography>
                )
            ) : (
                <>
                    <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                        A tela aparece aqui ao vivo enquanto o robô trabalha. Quando uma execução dá erro, a última foto fica nos arquivos dela.
                        {tela?.data ? ` Última foto da máquina: ${dataHora(tela.data)}${tela.robos ? ` (trabalhando: ${tela.robos})` : ""}.` : ""}
                    </MDTypography>
                    {tela?.imagem && (
                        <MDButton variant="text" color="info" size="small" onClick={() => setVerUltima((v) => !v)}>
                            <Icon sx={{ mr: 0.5 }}>{verUltima ? "visibility_off" : "visibility"}</Icon>
                            {verUltima ? "Esconder a última foto" : "Ver a última foto"}
                        </MDButton>
                    )}
                    {verUltima && imagem}
                </>
            )}
        </MDBox>
    );
}

/** Detalhe de um robô: situação atual e histórico de execuções com filtros. */
function DetalheRobo({ robo, agora, onFechar, onErro }) {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const hoje = new Date();
    const [de, setDe] = useState(dataIso(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 7)));
    const [ate, setAte] = useState(dataIso(hoje));
    const [situacao, setSituacao] = useState(FILTROS_SITUACAO[0]);
    const [pagina, setPagina] = useState(0);
    const [resultado, setResultado] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [execucaoAberta, setExecucaoAberta] = useState(null);

    const buscar = useCallback((numeroPagina) => {
        if (!robo) return;
        setCarregando(true);
        post("/robo-monitor/execucoes", { robo: robo.chave, de, ate, situacoes: situacao.situacoes, page: numeroPagina, pageSize: POR_PAGINA })
            .then((r) => { setResultado(r); setPagina(numeroPagina); })
            .catch((e) => onErro(e?.message || "Não foi possível carregar o histórico."))
            .finally(() => setCarregando(false));
    }, [robo, de, ate, situacao, onErro]);

    useEffect(() => {
        setResultado(null);
        if (robo) buscar(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [robo?.chave]);

    const fecharExecucao = useCallback(() => setExecucaoAberta(null), []);
    if (!robo) return null;
    const f = FAROIS[robo.farol] || FAROIS.AGUARDANDO;
    const totalPaginas = resultado ? Math.max(1, Math.ceil(resultado.total / POR_PAGINA)) : 1;
    const bordaLinha = { borderColor: cores.divisoria };

    return (
        <Dialog open={!!robo} onClose={onFechar} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {robo.nome}
                <Selo icone={f.icone} tom={f.tom} rotulo={robo.farolLabel} girar={robo.farol === "EM_EXECUCAO"} />
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    {robo.farolDescricao && (
                        <Grid item xs={12}>
                            <MDTypography variant="caption" color="text">{robo.farolDescricao}</MDTypography>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={3}><Campo titulo="Agenda">{robo.agendaDescricao}</Campo></Grid>
                    <Grid item xs={12} sm={6} md={3}><Campo titulo="Máquina">{robo.maquina || "-"}</Campo></Grid>
                    <Grid item xs={12} sm={6} md={3}><Campo titulo="Último início">{robo.ultimoInicio ? `${dataHora(robo.ultimoInicio)} (${tempoRelativo(robo.ultimoInicio, agora)})` : "-"}</Campo></Grid>
                    <Grid item xs={12} sm={6} md={3}><Campo titulo="Próxima prevista">{robo.farol === "PARADO" ? "Quando o serviço voltar" : robo.proximaExecucao ? dataHora(robo.proximaExecucao) : "-"}</Campo></Grid>
                    <Grid item xs={12} sm={6} md={3}><Campo titulo="Último sucesso">{robo.ultimoSucesso ? dataHora(robo.ultimoSucesso) : "-"}</Campo></Grid>
                    <Grid item xs={12} sm={6} md={3}><Campo titulo="Último erro">{robo.ultimoErro ? dataHora(robo.ultimoErro) : "-"}</Campo></Grid>
                    <Grid item xs={12} sm={12} md={6}><Campo titulo="Identificação no código">{robo.chave}</Campo></Grid>
                    {robo.ultimoErroTexto && (
                        <Grid item xs={12}>
                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", mb: 0.5 }}>Último erro registrado</MDTypography>
                            <TextoErro texto={robo.ultimoErroTexto} />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <TelaAgora robo={robo} />
                    </Grid>

                    <Grid item xs={12}>
                        <Card sx={{ px: 2, py: 1.5, background: cores.painel, boxShadow: "none", border: `1px solid ${cores.divisoria}` }}>
                            <Grid container spacing={2} alignItems="flex-end">
                                <Grid item xs={12} sm={6} md={3}>
                                    <InputLabel sx={rotulo}>De</InputLabel>
                                    <MDInput size="small" type="date" fullWidth value={de} onChange={(e) => setDe(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <InputLabel sx={rotulo}>Até</InputLabel>
                                    <MDInput size="small" type="date" fullWidth value={ate} onChange={(e) => setAte(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <InputLabel sx={rotulo}>Situação</InputLabel>
                                    <MDSelect name="situacao" value={situacao} options={FILTROS_SITUACAO} onChange={(v) => setSituacao(v || FILTROS_SITUACAO[0])} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <MDButton variant="contained" color="info" size="medium" fullWidth onClick={() => buscar(0)} disabled={carregando}>
                                        <Icon sx={{ mr: 0.5 }}>search</Icon>Buscar
                                    </MDButton>
                                </Grid>
                                <Grid item xs={12} sx={{ pt: "8px !important" }}>
                                    <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                                        Robôs que rodam a cada poucos minutos só registram aqui as execuções com erro, com trabalho informado
                                        (processados, arquivos) ou que demoraram mais de 2 minutos; a última execução aparece sempre no cartão.
                                        O histórico é guardado por 90 dias.
                                    </MDTypography>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <MDBox height={4}>{carregando && <LinearProgress color="info" />}</MDBox>
                        {resultado && resultado.itens.length === 0 ? (
                            <MDBox py={4} textAlign="center">
                                <MDTypography variant="button" color="text">Nenhuma execução registrada com esses filtros.</MDTypography>
                            </MDBox>
                        ) : resultado && (
                            <TableContainer sx={{ boxShadow: "none", borderRadius: 0, background: "transparent" }}>
                                <Table sx={{ minWidth: 820 }}>
                                    <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow>
                                            {["Início", "Duração", "Situação", "Processados", "Resumo / erro", "Arquivos", ""].map((t, i) => (
                                                <DataTableHeadCell key={i} sorted={false}>{t}</DataTableHeadCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {resultado.itens.map((e) => {
                                            const s = SITUACOES[e.situacao] || {};
                                            return (
                                                <TableRow key={e.id} hover sx={{ cursor: "pointer" }} onClick={() => setExecucaoAberta(e.id)}>
                                                    <TableCell sx={{ ...celula, ...bordaLinha, whiteSpace: "nowrap" }}>
                                                        <MDTypography variant="caption" color="text">{dataHora(e.inicio)}</MDTypography>
                                                    </TableCell>
                                                    <TableCell sx={{ ...celula, ...bordaLinha, whiteSpace: "nowrap" }}>
                                                        <MDTypography variant="caption" color="text">{formatarDuracao(e.duracao)}</MDTypography>
                                                    </TableCell>
                                                    <TableCell sx={{ ...celula, ...bordaLinha }}>
                                                        <Selo icone={s.icone || "help"} tom={s.tom} rotulo={e.situacaoLabel || "-"} />
                                                    </TableCell>
                                                    <TableCell sx={{ ...celula, ...bordaLinha }}>
                                                        <MDTypography variant="caption" color="text">{e.processados ?? "-"}</MDTypography>
                                                    </TableCell>
                                                    <TableCell sx={{ ...celula, ...bordaLinha, maxWidth: 420 }}>
                                                        <MDTypography variant="caption" color="text" sx={{ display: "block" }}>{e.resumo || (e.erroResumo ? "" : "-")}</MDTypography>
                                                        {e.erroResumo && (
                                                            <MDTypography variant="caption" sx={{ display: "block", color: cores.textoSecundario, fontStyle: "italic" }}>{e.erroResumo}</MDTypography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ ...celula, ...bordaLinha }}>
                                                        <MDTypography variant="caption" color="text">{e.qtdArquivos > 0 ? e.qtdArquivos : "-"}</MDTypography>
                                                    </TableCell>
                                                    <TableCell sx={{ ...celula, ...bordaLinha, pt: 0.5 }}>
                                                        <Tooltip title="Ver execução">
                                                            <IconButton size="small" onClick={(ev) => { ev.stopPropagation(); setExecucaoAberta(e.id); }}>
                                                                <Icon fontSize="small">open_in_new</Icon>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        {resultado && resultado.total > POR_PAGINA && (
                            <MDBox display="flex" justifyContent="flex-end" alignItems="center" gap={1} pt={1}>
                                <MDTypography variant="caption" color="text">
                                    Página {pagina + 1} de {totalPaginas} · {resultado.total} execuções
                                </MDTypography>
                                <IconButton size="small" disabled={pagina === 0 || carregando} onClick={() => buscar(pagina - 1)}>
                                    <Icon>chevron_left</Icon>
                                </IconButton>
                                <IconButton size="small" disabled={pagina + 1 >= totalPaginas || carregando} onClick={() => buscar(pagina + 1)}>
                                    <Icon>chevron_right</Icon>
                                </IconButton>
                            </MDBox>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <MDButton variant="text" color="info" onClick={onFechar}>Fechar</MDButton>
            </DialogActions>
            <DetalheExecucao id={execucaoAberta} onFechar={fecharExecucao} onErro={onErro} />
        </Dialog>
    );
}

/**
 * Monitor de Robôs (Robôs > Monitor de Robôs, c083): situação de cada robô agendado no ral-robot, sinal de vida do
 * serviço, histórico de execuções e arquivos. Os alertas por e-mail vão para ROBO_MONITOR_EMAILS_ALERTA.
 */
function MonitorRobos() {
    const [controller] = useMaterialUIController();
    const cores = coresDoTema(controller.darkMode);
    const [painel, setPainel] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [busca, setBusca] = useState("");
    const [grupo, setGrupo] = useState(GRUPO_TODOS);
    const [atualizacao, setAtualizacao] = useState(ATUALIZACAO_OPTIONS[2]);
    const [aberto, setAberto] = useState(null);
    const [erro, setErro] = useState("");

    const carregar = useCallback(() => {
        setCarregando(true);
        get("/robo-monitor/painel")
            .then(setPainel)
            .catch((e) => setErro(e?.message || "Não foi possível carregar o monitor."))
            .finally(() => setCarregando(false));
    }, []);

    useEffect(() => { carregar(); }, [carregar]);
    useEffect(() => {
        if (!atualizacao.value) return undefined;
        const id = setInterval(carregar, atualizacao.value * 1000);
        return () => clearInterval(id);
    }, [atualizacao, carregar]);

    const robos = useMemo(() => ordenarRobos(painel?.robos), [painel]);
    const totais = useMemo(() => contarGrupos(robos), [robos]);
    const visiveis = robos.filter((r) => (!grupo.value || grupoDoFarol(r.farol) === grupo.value) && correspondeBusca(r, busca));
    // O robô aberto acompanha a atualização automática do painel
    const roboAberto = aberto ? robos.find((r) => r.chave === aberto) || null : null;
    const fecharRobo = useCallback(() => setAberto(null), []);
    const mostrarErro = useCallback((m) => setErro(m || "Erro."), []);
    const agora = painel?.agora;

    return (
        <MDBox pb={3}>
            <MDSnackbar color="error" icon="warning" title="Monitor de Robôs" content={erro} open={!!erro}
                onClose={() => setErro("")} close={() => setErro("")} bgWhite />
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    {painel ? <Instancias instancias={painel.instancias || []} agora={agora} /> : (
                        <Card sx={{ p: 2, background: cores.cartao }}>
                            <MDBox display="flex" justifyContent="center"><CircularProgress color="info" size={28} /></MDBox>
                        </Card>
                    )}
                </Grid>

                {Object.keys(GRUPOS).map((g) => (
                    <Grid item xs={12} sm={6} lg={3} key={g}>
                        <Indicador grupo={g} valor={painel ? totais[g] : "-"} selecionado={grupo.value === g}
                            onClick={() => setGrupo(grupo.value === g ? GRUPO_TODOS : GRUPO_OPTIONS.find((o) => o.value === g))} />
                    </Grid>
                ))}

                <Grid item xs={12}>
                    <Card sx={{ px: 2, py: 1.5, background: cores.cartao }}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} sm={6} md={4}>
                                <InputLabel sx={rotulo}>Buscar</InputLabel>
                                <MDInput size="small" fullWidth value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome do robô, máquina ou resumo" />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <InputLabel sx={rotulo}>Situação</InputLabel>
                                <MDSelect name="grupo" value={grupo} options={GRUPO_OPTIONS} onChange={(v) => setGrupo(v || GRUPO_TODOS)} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <InputLabel sx={rotulo}>Atualizar a cada</InputLabel>
                                <MDSelect name="atualizacao" value={atualizacao} options={ATUALIZACAO_OPTIONS} onChange={(v) => setAtualizacao(v || ATUALIZACAO_OPTIONS[0])} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <MDButton variant="contained" color="info" size="medium" fullWidth onClick={carregar} disabled={carregando}>
                                    <Icon sx={{ mr: 0.5 }}>refresh</Icon>Atualizar
                                </MDButton>
                            </Grid>
                            <Grid item xs={12} sx={{ pt: "8px !important" }}>
                                <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                                    Clique em um robô para ver o histórico, os erros e os arquivos de cada execução (ex.: o CSV enviado ao WIBS e
                                    o retorno). Os alertas de erro, atraso, robô travado e serviço parado vão por e-mail para os endereços de
                                    Parametrização do Sistema &gt; E-mail - Alertas do Monitor de Robôs, uma vez por ocorrência e outra quando normaliza.
                                </MDTypography>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <MDBox height={4}>{carregando && painel && <LinearProgress color="info" />}</MDBox>
                </Grid>
                {painel && visiveis.length === 0 && (
                    <Grid item xs={12}>
                        <Card sx={{ p: 4, background: cores.cartao, textAlign: "center" }}>
                            <MDTypography variant="button" color="text">
                                {robos.length === 0
                                    ? "Nenhum robô registrado. Os robôs aparecem aqui quando o serviço dos robôs sobe com a versão do monitor."
                                    : "Nenhum robô com esses filtros."}
                            </MDTypography>
                        </Card>
                    </Grid>
                )}
                {visiveis.map((r) => (
                    <Grid item xs={12} md={6} xl={4} key={r.chave}>
                        <CartaoRobo robo={r} agora={agora} onAbrir={() => setAberto(r.chave)} />
                    </Grid>
                ))}
            </Grid>
            <DetalheRobo robo={roboAberto} agora={agora} onFechar={fecharRobo} onErro={mostrarErro} />
        </MDBox>
    );
}

export default MonitorRobos;
